import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse, Method} from 'axios';

import requestApiTimeouts from '../../consts/config/requestApiTimeouts';
import {GET_TOKEN} from '../../consts/icc/iccMethods';
import * as logMessageTypes from '../../consts/log/logMessageTypes';
import NetworkOfflineError from '../errors/networkOfflineError';
import log from '../logger/log';
import networkStatusService from '../networkStatusService';
import httpStatusTypesService from './httpStatusTypesService';

const get: AxiosInstance['get'] = (url, requestConfig): Promise<any> => requestApi('get', url, requestConfig);
const post: AxiosInstance['post'] = (url, requestConfig = {} as any): Promise<any> =>
    requestApi('post', url, requestConfig as any);
const put: AxiosInstance['put'] = (url, requestConfig = {} as any): Promise<any> =>
    requestApi('put', url, requestConfig as any);
const requestDelete: AxiosInstance['delete'] = (url, requestConfig = {}): Promise<any> =>
    requestApi('delete', url, requestConfig);

const requestApi = async <T = any, R = AxiosResponse<T>>(
    method: Method,
    url: string,
    requestConfig: AxiosRequestConfig = {}
): Promise<R> => {
    const isGetToken = url.includes(GET_TOKEN);
    let pendingTimeout;

    const isOnline = networkStatusService.isOnline();

    if (!isOnline) {
        throw new NetworkOfflineError();
    }

    try {
        const controller = new AbortController();

        pendingTimeout = setTimeout(() => controller.abort(), requestApiTimeouts.PENDING_REQUEST_TIMEOUT);

        log.debug(`Request has started. Url: ${url}`);

        const response: R = await request({
            method,
            url,
            ...requestConfig,
            signal: controller.signal,
            timeout: requestApiTimeouts.PENDING_REQUEST_TIMEOUT,
        });

        clearTimeout(pendingTimeout);

        //icc token should be already exists to post logs
        if (!isGetToken) {
            log.info(`Request has successful response. Url: ${url}`);
        }
        return response;
    } catch (e: any) {
        if (pendingTimeout) {
            clearTimeout(pendingTimeout);
        }

        const LOG_LENGTH_LIMIT = 10 * 1024;
        const requestConfigCopy = {...requestConfig};

        if (requestConfig?.headers) {
            const trimRequestConfigHeader = (headerName: string) => {
                const header = requestConfig.headers?.[headerName]?.toString();

                if (header) {
                    const NUMBER_OF_VISIBLE_CHARS = 10;
                    const headerTrimmed = `${header.substring(0, NUMBER_OF_VISIBLE_CHARS)}*****${header.substring(
                        header.length - NUMBER_OF_VISIBLE_CHARS
                    )}`;

                    requestConfigCopy.headers![headerName] = headerTrimmed;
                }
            };

            trimRequestConfigHeader('Authorization');
            trimRequestConfigHeader('Token');
            trimRequestConfigHeader('id_token');
            trimRequestConfigHeader('X-API-Key');
            trimRequestConfigHeader('jwt');
        }

        if (isGetToken) {
            requestConfigCopy.data = undefined;
        }

        const logMessage = `requestApi: url: ${url}; requestConfig: ${JSON.stringify(
            requestConfigCopy
        )}, method: ${method}; error: ${e}; response data: ${JSON.stringify(e.response?.data)?.substring(
            0,
            LOG_LENGTH_LIMIT
        )}`;

        const statusCode = e?.response?.status;
        const isInfoLevel = !statusCode || httpStatusTypesService.isNotAuthorized(statusCode);

        if (isInfoLevel) {
            log.info(logMessage);
        } else {
            log.error(logMessage, {logMessageType: logMessageTypes.EXTERNAL});
        }

        throw e;
    }
};

const request: AxiosInstance['request'] = (requestConfig) => axios.request(requestConfig);

export default {
    get,
    post,
    put,
    requestDelete,
};
